import React from 'react';
import { Persistor } from '../../stores/store';

interface IAppErrorBoundaryState {
	hasError: boolean;
}

// Top-level, app-wide safety net. The whole redux tree is persisted (redux-persist, see
// stores/store.js) and rehydrated on every load - so if a bad/stale shape ever gets into it
// (an old cached session predating a backend/frontend change, a state update that only half
// landed, etc.) and a component crashes rendering it, a plain refresh does NOT help: it just
// rehydrates the exact same bad state and crashes again. Per-page ErrorComponent boundaries
// catch the crash but don't fix the underlying cause, so the user is stuck seeing the same
// broken page until someone tells them to log out and back in (which resets the persisted
// state). This catches anything that escapes those (or isn't wrapped by one at all) and gives
// a one-click way to clear the corrupted cache and start fresh, instead of a blank page.
class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, IAppErrorBoundaryState> {
	state: IAppErrorBoundaryState = { hasError: false };

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error, info) {
		console.error('Unhandled render error - offering a reset', error, info);
	}

	handleReset = () => {
		Persistor.purge().finally(() => {
			window.location.reload();
		});
	};

	render() {
		if (this.state.hasError) {
			return (
				<div style={{ padding: '60px 20px', textAlign: 'center', fontFamily: 'sans-serif' }}>
					<h2>Something went wrong.</h2>
					<p style={{ color: '#666', maxWidth: '480px', margin: '10px auto 25px' }}>
						This is usually caused by stale locally cached data. Resetting clears only that
						local cache - it will not affect your account or any saved data.
					</p>
					<button
						onClick={this.handleReset}
						style={{
							padding: '10px 24px',
							fontSize: '15px',
							cursor: 'pointer',
							backgroundColor: '#9c27b0',
							color: '#fff',
							border: 'none',
							borderRadius: '4px',
						}}
					>
						Reset and reload
					</button>
				</div>
			);
		}
		return this.props.children;
	}
}

export default AppErrorBoundary;
